/**
 * Pure helpers for Data Access Assignments.
 *
 * These functions carry the input-varying logic for the assignment form:
 * name/description validation, scope-mode gating, and scope-selection checks.
 * They are intentionally free of React and network concerns so they can be
 * unit- and property-tested in isolation.
 */

// The five asset-scope id arrays that make up a "selected" scope.
export const SCOPE_ID_KEYS = [
  "vehicle_group_ids",
  "vehicle_ids",
  "device_group_ids",
  "device_ids",
  "event_ids",
];

/**
 * A display name is valid when its trimmed length is between 1 and 100
 * (inclusive). Non-string values are rejected.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidName = (value) => {
  if (typeof value !== "string") return false;
  const length = value.trim().length;
  return length >= 1 && length <= 100;
};

/**
 * A description is valid when its length is at most 500 (inclusive), including
 * the empty string. Non-string values are rejected.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isValidDescription = (value) => {
  if (typeof value !== "string") return false;
  return value.length <= 500;
};

/**
 * The asset scope selectors are enabled exactly when the scope mode is
 * "selected". For "all_client" and "none" (and any other value) they are
 * disabled.
 *
 * @param {string} scopeMode
 * @returns {boolean}
 */
export const areScopeSelectorsEnabled = (scopeMode) => scopeMode === "selected";

/**
 * Returns true when at least one of the five asset-scope id arrays holds a
 * selection. Missing or non-array fields are treated as empty.
 *
 * @param {object} values
 * @returns {boolean}
 */
export const hasAnyScopeSelection = (values) => {
  if (!values || typeof values !== "object") return false;
  return SCOPE_ID_KEYS.some((key) => Array.isArray(values[key]) && values[key].length > 0);
};

/**
 * Builds the create/update request body for a Data Access Assignment.
 *
 * The name is trimmed and `role_ids` is preserved unchanged. The five
 * asset-scope arrays are passed through directly.
 *
 * Note: description, scope_mode, and status are NOT sent in the payload —
 * they are not part of the PDF mockup and are managed by the backend.
 *
 * @param {object} values
 * @returns {{
 *   name: string,
 *   vehicle_group_ids: Array,
 *   vehicle_ids: Array,
 *   device_group_ids: Array,
 *   device_ids: Array,
 *   event_ids: Array,
 *   role_ids: Array,
 * }}
 */
export const buildAssignmentPayload = (values) => {
  return {
    name: values.name.trim(),
    vehicle_group_ids: values.vehicle_group_ids || [],
    vehicle_ids: values.vehicle_ids || [],
    device_group_ids: values.device_group_ids || [],
    device_ids: values.device_ids || [],
    event_ids: values.event_ids || [],
    role_ids: values.role_ids || [],
  };
};

/**
 * Adds a role id to a role set using set-union semantics: the result contains
 * every id from the input plus `roleId`, with no duplicates. The operation is
 * order-independent and never mutates the input array. A non-array input is
 * treated as empty.
 *
 * @param {Array} roleIds
 * @param {*} roleId
 * @returns {Array} a new array
 */
export const addRole = (roleIds, roleId) => {
  const current = Array.isArray(roleIds) ? roleIds : [];
  // De-duplicate the existing set and append the candidate when absent, so the
  // result is always a proper set regardless of duplicates in the input.
  return Array.from(new Set([...current, roleId]));
};

/**
 * Removes a role id from a role set using set-difference semantics: the result
 * contains every id from the input except `roleId`. The operation never
 * mutates the input array. A non-array input is treated as empty.
 *
 * @param {Array} roleIds
 * @param {*} roleId
 * @returns {Array} a new array
 */
export const removeRole = (roleIds, roleId) => {
  const current = Array.isArray(roleIds) ? roleIds : [];
  return current.filter((id) => id !== roleId);
};
/**
 * The five scoped asset types that make up effective access, in the fixed
 * order they are presented to the user.
 */
export const EFFECTIVE_ACCESS_TYPES = [
  "vehicle_groups",
  "vehicles",
  "device_groups",
  "devices",
  "events",
];

/**
 * Coerces an arbitrary value into a non-negative integer count. Anything that
 * is not already a non-negative integer (negatives, fractions, NaN, Infinity,
 * strings, booleans, objects, null/undefined) collapses to zero.
 *
 * @param {*} value
 * @returns {number}
 */
const coerceCount = (value) => {
  const count = typeof value === "number" ? value : NaN;
  if (!Number.isInteger(count) || count < 0) return 0;
  return count;
};

/**
 * Reads a count out of a single effective-access entry. The backend-dependent
 * shape is either a `{ count }` object (per the design's EffectiveAccess
 * model) or, defensively, a bare number. Any other shape yields zero.
 *
 * @param {*} entry
 * @returns {number}
 */
const extractCount = (entry) => {
  if (typeof entry === "number") return coerceCount(entry);
  if (entry && typeof entry === "object") return coerceCount(entry.count);
  return 0;
};

/**
 * Normalizes a backend-dependent effective-access payload into exactly the
 * five scoped asset types, each with a non-negative integer count.
 *
 * The result always contains one `{ type, count }` entry per asset type in
 * {@link EFFECTIVE_ACCESS_TYPES} order. Missing or absent types default to a
 * count of zero, extra keys on the input are ignored, and invalid, negative,
 * or non-integer counts are coerced to zero. An empty, nullish, or non-object
 * input yields zero for every type.
 *
 * @param {object} effectiveAccess
 * @returns {Array<{ type: string, count: number }>}
 */
export const deriveEffectiveAccessCounts = (effectiveAccess) => {
  const source =
    effectiveAccess && typeof effectiveAccess === "object" ? effectiveAccess : {};
  return EFFECTIVE_ACCESS_TYPES.map((type) => ({
    type,
    count: extractCount(source[type]),
  }));
};
